import React from 'react';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';

const TermsOfService = () => {
  const lastUpdated = 'January 2024';

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Terms of Service"
        description="Read Finclify terms of service to understand the rules and regulations for using our financial tools and services."
        keywords="terms of service, terms and conditions, finclify terms"
        type="website"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Terms of Service', url: '/terms' }
        ]}
      />
      <Breadcrumb
        items={[
          { name: 'Home', url: '/' },
          { name: 'Terms of Service', url: '/terms' }
        ]}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-blue-100 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Terms of Service
          </h1>
          <p className="text-lg text-gray-600">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12 space-y-8">
            
            <div>
              <p className="text-gray-600 leading-relaxed">
                Welcome to Finclify. These Terms of Service ("Terms") govern your access to and use of 
                our website, calculators, and services. By accessing or using our services, you agree to be 
                bound by these Terms.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-600">
                By accessing and using Finclify, you accept and agree to be bound by the terms and provision 
                of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Use License</h2>
              <p className="text-gray-600 mb-4">
                Permission is granted to temporarily access and use Finclify for personal, non-commercial 
                transitory viewing only. This is the grant of a license, not a transfer of title, and under 
                this license you may not:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li>Modify or copy the materials</li>
                <li>Use the materials for any commercial purpose or for any public display</li>
                <li>Attempt to reverse engineer any software contained on Finclify</li>
                <li>Remove any copyright or other proprietary notations from the materials</li>
                <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Disclaimer</h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  <strong className="text-gray-900">Not Financial Advice:</strong> The information provided on 
                  Finclify is for general informational purposes only and should not be considered as financial, 
                  investment, tax, or legal advice. All calculations and comparisons are estimates and should not 
                  be relied upon as the sole basis for financial decisions.
                </p>
                <p>
                  <strong className="text-gray-900">Accuracy:</strong> While we strive to provide accurate and 
                  up-to-date information, we make no representations or warranties of any kind, express or implied, 
                  about the completeness, accuracy, reliability, or suitability of the information, products, services, 
                  or related graphics contained on Finclify.
                </p>
                <p>
                  <strong className="text-gray-900">No Guarantees:</strong> We do not guarantee that the results 
                  from our calculators will match actual loan terms, interest rates, or financial product features. 
                  Always consult with qualified financial advisors before making financial decisions.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Limitations</h2>
              <p className="text-gray-600">
                In no event shall Finclify or its suppliers be liable for any damages (including, without limitation, 
                damages for loss of data or profit, or due to business interruption) arising out of the use or inability 
                to use the materials on Finclify, even if Finclify or a Finclify authorized representative 
                has been notified orally or in writing of the possibility of such damage.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Accuracy of Materials</h2>
              <p className="text-gray-600">
                The materials appearing on Finclify could include technical, typographical, or photographic errors. 
                Finclify does not warrant that any of the materials on its website are accurate, complete, or current. 
                Finclify may make changes to the materials contained on its website at any time without notice.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Links</h2>
              <p className="text-gray-600">
                Finclify has not reviewed all of the sites linked to our website and is not responsible for the 
                contents of any such linked site. The inclusion of any link does not imply endorsement by Finclify 
                of the site. Use of any such linked website is at the user's own risk.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Modifications</h2>
              <p className="text-gray-600">
                Finclify may revise these Terms of Service at any time without notice. By using this website, 
                you are agreeing to be bound by the then current version of these Terms of Service.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. User Conduct</h2>
              <p className="text-gray-600 mb-4">
                You agree not to use Finclify to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon the rights of others</li>
                <li>Transmit any harmful or malicious code</li>
                <li>Interfere with or disrupt the service</li>
                <li>Attempt to gain unauthorized access to any part of the service</li>
                <li>Use automated systems to access the service without permission</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Intellectual Property</h2>
              <p className="text-gray-600">
                All content, features, and functionality of Finclify, including but not limited to text, graphics, 
                logos, icons, images, and software, are the exclusive property of Finclify and are protected by 
                international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Termination</h2>
              <p className="text-gray-600">
                We may terminate or suspend your access to Finclify immediately, without prior notice or liability, 
                for any reason whatsoever, including without limitation if you breach the Terms. Upon termination, 
                your right to use the service will immediately cease.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law</h2>
              <p className="text-gray-600">
                These Terms shall be governed and construed in accordance with applicable laws, without regard to 
                its conflict of law provisions. Our failure to enforce any right or provision of these Terms will 
                not be considered a waiver of those rights.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
              <p className="text-gray-600">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-600">
                  <strong>Email:</strong> legal@Finclify.com
                </p>
                <p className="text-gray-600 mt-2">
                  <strong>Website:</strong> <a href="/contact" className="text-primary-600 hover:text-primary-700">Contact Page</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;

